import { Icon } from '@iconify/react';
import { DateTime } from 'luxon';
import Moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DataTable } from '../../../../components/DataTable';
import { Loading } from '../../../../components/Loading';
import { StyledButton } from '../../../../components/StyledButton';
import { StyledDateInput } from '../../../../components/StyledDateInput';
import { StyledSelect } from '../../../../components/StyledSelect';
import { useUserDocument } from '../../../../services/contexts/UserDocumentContext';
import { AdminTitle } from '../../components/AdminTitle';
import { Container, FiltersContainer, Row, Td } from './styles';

import {
  IAccompanimentProps,
  useAcompanhamento,
} from '../../../../services/hooks/useAcompanhamento';
import { AddLocaleDateString } from '../../../../utils/AddLocaleDateString';
import { NewFormatValue } from '../../../../utils/NewFormatValue';
import { NinetyDaysAgo } from '../../../../utils/NinetyDaysAgo';
import { InputDescription } from '../../components/InputDescription';

const tableHead = [
  'Nº de Solicitação',
  'Valor Bruto',
  'Valor Líquido',
  'Data da Solicitação',
  'Data da Antecipação',
  'Status',
];

export const Acompanhamento = () => {
  const today = new Date();
  const [initialDate, setInitialDate] = useState<Date>(NinetyDaysAgo(today));
  const [endDate, setEndDate] = useState<Date>(today);
  const { documentSelected } = useUserDocument();
  const docto = useMemo(() => documentSelected as string, [documentSelected]);

  const [selectStatus, setSelectStatus] = useState<string>('A');
  const [followUpList, setFollowUpList] = useState<
    IAccompanimentProps[] | undefined
  >([]);

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const now = DateTime.now()
    .setLocale('pt-br')
    .toLocaleString(DateTime.DATE_HUGE);

  const [params, setParams] = useState({
    docto: docto ?? '',
    dtInicial: AddLocaleDateString(initialDate),
    dtFinal: AddLocaleDateString(endDate),
    tipoPeriodo: selectStatus,
    pagina: page,
    limite: itemsPerPage,
  });

  const { data, isLoading, isFetching, error, refetch } =
    useAcompanhamento(params);

  const handleFilter = useCallback(() => {
    const newParams = {
      ...params,
      docto: docto,
      dtInicial: AddLocaleDateString(initialDate),
      dtFinal: AddLocaleDateString(endDate),
      tipoPeriodo: selectStatus,
      pagina: page,
      limite: itemsPerPage,
    };
    setParams(newParams);
  }, [docto, initialDate, endDate, selectStatus, page, itemsPerPage]);

  useEffect(() => {
    if (docto) {
      setParams(prev => ({
        docto: docto,
        dtInicial: AddLocaleDateString(initialDate),
        dtFinal: AddLocaleDateString(endDate),
        tipoPeriodo: selectStatus,
        pagina: page,
        limite: itemsPerPage,
      }));
    }
  }, [docto, page, itemsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [itemsPerPage])

  useEffect(() => {
    setFollowUpList(data);
  }, [data]);

  return (
    <Container>
      <p className="date">{now}</p>

      <AdminTitle
        title="Movimentação Financeira > Acompanhamento de Antecipação"
        icon={<Icon icon="tabler:pig-money" />}
      />

      <FiltersContainer>
        <div>
          <InputDescription placeholder="Data Inicial" />
          <StyledDateInput
            name="initialDate"
            value={initialDate}
            onChange={setInitialDate}
            placeholder="Data inicial"
            minDate={NinetyDaysAgo(today) ?? today}
          />
        </div>
        <div>
          <InputDescription placeholder="Data Final" />
          <StyledDateInput
            name="data"
            value={endDate}
            onChange={setEndDate}
            placeholder="Data final"
            minDate={NinetyDaysAgo(today) ?? today}
          />
        </div>

        <div>
          <InputDescription placeholder="Filtro por tipo" />
          <StyledSelect
            options={[
              { id: 'E', value: 'Emissão' },
              { id: 'A', value: 'Antecipação' },
            ]}
            setValue={(e) => setSelectStatus(e)}
            value={selectStatus}
          />
        </div>

        <StyledButton
          icon={<Icon icon="clarity:filter-solid" width={18} />}
          iconPosition="right"
          text="Filtrar"
          onClick={handleFilter}
        />
      </FiltersContainer>

      <DataTable
        title="Acompanhamento de Antecipações"
        heads={tableHead}
        hasPagination
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        page={page}
        setPage={setPage}
        totalItems={followUpList?.length}
      >
        <tbody>
          {isLoading || isFetching ? (
            <Loading />
          ) : error ? (
            <Row>
              <Td colSpan={11}>Falha ao obter dados</Td>
            </Row>
          ) : followUpList == null ? (
            <Row>
              <Td colSpan={11}>Não foram encontrados dados.</Td>
            </Row>
          ) : (
            followUpList?.map((dl: any, i: number) => (
              <Row key={i} status={dl?.situacao}>
                <Td>{dl?.id}</Td>
                <Td>{NewFormatValue(dl?.vlTotBruto)}</Td>
                <Td>{NewFormatValue(dl?.vlTotLiqReal)}</Td>
                <Td>{Moment(dl?.dtOperInc).format('DD/MM/yyyy [às] hh:mm')}</Td>
                <Td>{Moment(dl?.dtAntecipacao).format('DD/MM/yyyy')}</Td>
                <Td>{dl?.situacaoDescricao}</Td>
              </Row>
            ))
          )}
        </tbody>
      </DataTable>
    </Container>
  );
};
